import { useCallback, useEffect, useState } from "react";

import { useDropzone } from "react-dropzone";
import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import {
  AlertCircle,
  CalendarIcon,
  CheckCircle2,
  Download,
  Loader2,
  Upload,
  X,
} from "lucide-react";
import { z } from "zod";

import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useAccountsByCompany } from "~/hooks/contabilidad/useAccounts";
import { useCompanies } from "~/hooks/contabilidad/useCompanies";
import { useJournalsByCompany } from "~/hooks/contabilidad/useJournals";
import { downloadMovimientoTemplate } from "~/lib/contabilidad/generateMovimientoTemplate";
import {
  type ParseResult,
  hasValidationErrors,
  parseMovimientoFile,
  toMoveLineInputs,
} from "~/lib/contabilidad/parseMovimientoFile";
import { cn } from "~/lib/utils";
import type { MovementFormData } from "~/types/contabilidad";

const formSchema = z.object({
  company_id: z.string().min(1, "Seleccione una empresa"),
  journal_id: z.string().min(1, "Seleccione un diario"),
  move_date: z.string().min(1, "Seleccione una fecha"),
  ref: z.string().min(2, "La referencia debe tener al menos 2 caracteres"),
});

interface MovimientoImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: MovementFormData) => void;
  isLoading?: boolean;
}

export function MovimientoImportDialog({
  open,
  onOpenChange,
  onSubmit,
  isLoading = false,
}: MovimientoImportDialogProps) {
  const [file, setFile] = useState<File | null>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const { data: companies = [], isLoading: isLoadingCompanies } =
    useCompanies();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      company_id: "",
      journal_id: "",
      move_date: dayjs().format("YYYY-MM-DD"),
      ref: "",
    },
  });

  const selectedCompanyId = form.watch("company_id");
  const { data: accounts = [] } = useAccountsByCompany(selectedCompanyId);
  const { data: journals = [], isLoading: isLoadingJournals } =
    useJournalsByCompany(selectedCompanyId);

  // Re-parse file when company changes (accounts are different)
  useEffect(() => {
    if (file && selectedCompanyId && accounts.length > 0) {
      handleParseFile(file);
    } else if (!selectedCompanyId) {
      setParseResult(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, accounts]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setFile(null);
      setParseResult(null);
      form.reset();
    }
  }, [open, form]);

  const handleParseFile = async (uploadedFile: File) => {
    if (!selectedCompanyId || accounts.length === 0) {
      return;
    }

    setIsParsing(true);
    try {
      const result = await parseMovimientoFile(uploadedFile, accounts);
      setParseResult(result);
    } catch (error) {
      console.error("Error parsing file:", error);
      setParseResult({
        lines: [],
        errors: ["Error al procesar el archivo"],
        totalDebit: 0,
        totalCredit: 0,
        isBalanced: true,
      });
    } finally {
      setIsParsing(false);
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const uploadedFile = acceptedFiles[0];
      if (uploadedFile) {
        setFile(uploadedFile);
        if (selectedCompanyId && accounts.length > 0) {
          handleParseFile(uploadedFile);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCompanyId, accounts]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
    maxFiles: 1,
    disabled: !selectedCompanyId,
  });

  const handleRemoveFile = () => {
    setFile(null);
    setParseResult(null);
  };

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      !parseResult ||
      hasValidationErrors(parseResult) ||
      !parseResult.isBalanced
    ) {
      return;
    }

    const lines = toMoveLineInputs(parseResult.lines);
    if (lines.length < 2) {
      return;
    }

    onSubmit({
      ...data,
      lines,
    });
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat("es-CL").format(value);
  };

  const canSubmit =
    parseResult &&
    !hasValidationErrors(parseResult) &&
    parseResult.isBalanced &&
    parseResult.lines.length >= 2;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-[900px]">
        <DialogHeader>
          <DialogTitle>Importar Comprobante</DialogTitle>
          <DialogDescription>
            Cargue un archivo Excel o CSV con las líneas del comprobante.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            {/* Header Fields */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="company_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Empresa *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={isLoadingCompanies}
                    >
                      <FormControl>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Seleccione una empresa" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.id}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="journal_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Diario *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={isLoadingJournals || !selectedCompanyId}
                    >
                      <FormControl>
                        <SelectTrigger className="rounded-xl">
                          <SelectValue placeholder="Seleccione un diario" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {journals.map((journal) => (
                          <SelectItem key={journal.id} value={journal.id}>
                            {journal.code} - {journal.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="move_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full rounded-xl pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              dayjs(field.value).format("DD/MM/YYYY")
                            ) : (
                              <span>Seleccione fecha</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={
                            field.value ? new Date(field.value) : undefined
                          }
                          onSelect={(date) =>
                            field.onChange(
                              date ? dayjs(date).format("YYYY-MM-DD") : ""
                            )
                          }
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ref"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Referencia *</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Descripción o referencia"
                        className="rounded-xl"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Template Download & File Upload */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <FormLabel>Archivo de Datos</FormLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={downloadMovimientoTemplate}
                  className="h-8 rounded-lg"
                >
                  <Download className="mr-2 h-4 w-4" />
                  Descargar Plantilla
                </Button>
              </div>

              {!file ? (
                <div
                  {...getRootProps()}
                  className={cn(
                    "cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors",
                    isDragActive
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/25 hover:border-primary/50",
                    !selectedCompanyId && "cursor-not-allowed opacity-50"
                  )}
                >
                  <input {...getInputProps()} />
                  <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
                  <p className="mt-2 text-sm text-muted-foreground">
                    {!selectedCompanyId
                      ? "Primero seleccione una empresa"
                      : isDragActive
                        ? "Suelte el archivo aquí..."
                        : "Arrastre un archivo aquí o haga clic para seleccionar"}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Formatos permitidos: .xlsx, .xls, .csv
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-xl border bg-muted/50 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                    <div>
                      <p className="text-sm font-medium">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={handleRemoveFile}
                    className="h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Parsing indicator */}
            {isParsing && (
              <div className="flex items-center justify-center gap-2 py-4">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm text-muted-foreground">
                  Procesando archivo...
                </span>
              </div>
            )}

            {/* Errors */}
            {parseResult && parseResult.errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-inside list-disc">
                    {parseResult.errors.map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* Preview Table */}
            {parseResult && parseResult.lines.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <FormLabel>
                    Vista Previa ({parseResult.lines.length} líneas)
                  </FormLabel>
                  {!parseResult.isBalanced && (
                    <span className="text-sm text-destructive">
                      Asiento descuadrado. Diferencia: $
                      {formatNumber(
                        Math.abs(
                          parseResult.totalDebit - parseResult.totalCredit
                        )
                      )}
                    </span>
                  )}
                </div>
                <div className="max-h-[300px] overflow-auto rounded-xl border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px]">Fila</TableHead>
                        <TableHead className="w-[100px]">Cuenta</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="w-[120px] text-right">
                          Debe
                        </TableHead>
                        <TableHead className="w-[120px] text-right">
                          Haber
                        </TableHead>
                        <TableHead className="w-[40px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parseResult.lines.map((line, idx) => (
                        <TableRow
                          key={idx}
                          className={cn(
                            "duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both",
                            line.error && "bg-destructive/10"
                          )}
                          style={{
                            animationDelay: `${Math.min(idx * 40, 400)}ms`,
                          }}
                        >
                          <TableCell className="text-muted-foreground">
                            {line.rowNumber}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {line.accountCode}
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate">
                            {line.description}
                          </TableCell>
                          <TableCell className="text-right">
                            {line.debit > 0 ? formatNumber(line.debit) : "-"}
                          </TableCell>
                          <TableCell className="text-right">
                            {line.credit > 0 ? formatNumber(line.credit) : "-"}
                          </TableCell>
                          <TableCell>
                            {line.error && (
                              <AlertCircle
                                className="h-4 w-4 text-destructive"
                                title={line.error}
                              />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={3} className="font-medium">
                          Total
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatNumber(parseResult.totalDebit)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatNumber(parseResult.totalCredit)}
                        </TableCell>
                        <TableCell>
                          {parseResult.isBalanced ? (
                            <CheckCircle2 className="h-4 w-4 text-green-500" />
                          ) : (
                            <AlertCircle className="h-4 w-4 text-destructive" />
                          )}
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  </Table>
                </div>

                {/* Line errors */}
                {parseResult.lines.some((l) => l.error) && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>
                      <ul className="list-inside list-disc">
                        {parseResult.lines
                          .filter((l) => l.error)
                          .map((line, idx) => (
                            <li key={idx}>
                              Fila {line.rowNumber}: {line.error}
                            </li>
                          ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="rounded-full"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="rounded-full px-6"
                disabled={isLoading || !canSubmit}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creando...
                  </>
                ) : (
                  "Crear Comprobante"
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
